import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showSuccess, showError } from "redux/actions/ui";

import {
  GetPowerCarts,
  PutPowerCart,
  DeletePowerCart,
  IPowerCart,
  GetPowerCartAudits,
  GetPowerCartAuditTrailExport,
} from "api/rpc/2024-04/facilityAdmin/facility/powercart/powercart";

import Page from "components/page/Page";
import Input from "components/form/input/Input";
import { StatusCode } from "api/protocols";
import PowerCartCard from "components/powerCartCards/PowerCartCard";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import { capitalize } from "helpers/Helpers";

import "./cartInventory.scss";
import moment, { MomentInput } from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ExportPowerCarts from "./powerCarts/exportPowerCarts";
import Tabs from "components/tabs/Tabs";
import ReactDOM from "react-dom";
import { faFileExport, faArrowLeft, faArrowRight, faCircleCheck, faCircleExclamation, faEngineWarning, faHand, faTire } from "@fortawesome/pro-regular-svg-icons";
import { ButtonNew } from "components/buttonNew";
import SelectNew from "components/select/SelectNew";
import DatePickerInput from "components/datePickerInput/DatePickerInput";

interface ICartInventoryState {
  availableCarts: IPowerCart[];
  maintenanceCarts: IPowerCart[];
  editing: boolean;
  deletePopupVisible: boolean;
  currentCartId: number;
}

interface IEditCartModalState {
  status: string;
  name: string;
  brand: string;
  type: string;
  modalVisible: boolean;
  notes: string;
  available_at: string;
}

interface IExportState {
  dateRangeStart: Date;
  dateRangeEnd: Date;
}

export default function CartInventory() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { authStore } = useAppSelector(store => store);
  const { Option } = Select;

  const GENERAL_INFORMATION = 0;
  const HISTORY = 1;
  const tabs = [
    {
      id: GENERAL_INFORMATION,
      name: "General Information",
      content: "General Information",
    },
    {
      id: HISTORY,
      name: "History",
      content: "History",
    },
  ];

  const today = new Date();

  const [cartInventoryState, setCartInventoryState] = useState<ICartInventoryState>({
    availableCarts: null,
    maintenanceCarts: null,
    editing: false,
    deletePopupVisible: false,
    currentCartId: null,
  });

  const [editCartState, setEditCartState] = useState<IEditCartModalState>({
    status: "",
    name: "",
    brand: "",
    type: "",
    modalVisible: false,
    notes: "",
    available_at: "",
  });

  const [exportState, setExportState] = useState<IExportState>({
    dateRangeStart: today,
    dateRangeEnd: today,

  });
  const [showDateExportRange, setShowDateExportRange] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(tabs[0].id);
  const [auditTrail, setAuditTrail] = useState<any[]>([]);

  const [auditLimit, setAuditLimit] = useState(10);
  const [auditOffset, setAuditOffset] = useState(0);
  const [finalAuditPage, setFinalAuditPage] = useState(false);

  const cartStatuses = ["available", "reserved", "occupied", "maintenance", "broken"];

  useEffect(() => {
    void loadCarts();
  }, []);

  function navigateToAddInventory() {
    if (authStore?.user?.permissions?.tee_sheet_starter) {
      history.push("/admin/starter-teesheet-cart-inventory/new");
    } else {
      history.push("/admin/settings/cart-inventory/new");
    }
    
  }

  const primaryAction = {
    content: "Add Inventory",
    action: navigateToAddInventory,
  };

  function setDateRangeStart(date: Date) {
    setExportState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setExportState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  async function loadCarts() {
    const powerCartRes = await GetPowerCarts({}, true);

    if (powerCartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading carts")));
      return;
    }

    setCartInventoryState(prevState => ({ ...prevState, availableCarts: powerCartRes.data }));
  }

  async function deleteCart() {
    const deleteRes = await DeletePowerCart({ id: cartInventoryState.currentCartId }, true);
    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting cart")));
      return;
    }

    dispatch(showSuccess(t("Cart deleted successfully")));

    void loadCarts();
    ReactDOM.unstable_batchedUpdates(() => {
      setCartInventoryState(prevState => ({ ...prevState, deletePopupVisible: false, currentCartId: null }));
      setEditCartState(prevState => ({ ...prevState, modalVisible: false, name: "", status: "" }));
      setSelectedTab(0);
      setAuditTrail([]);
      setAuditLimit(10);
      setAuditOffset(0);
    });
  }

  function handleDelete(cartId: number) {
    setEditCartState(prevState => ({ ...prevState, modalVisible: false }));
    setCartInventoryState(prevState => ({ ...prevState, currentCartId: cartId, deletePopupVisible: true }));
  }

  async function handleChangeStatus(id: number, status: string) {
    const powerCartRes = await PutPowerCart({ id: id, status: status }, true);

    if (powerCartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating cart status")));
      return;
    }

    dispatch(showSuccess(t("Cart status updated successfully")));

    void loadCarts();
  }

  async function updateCart() {
    const powerCartRes = await PutPowerCart(
      {
        id: cartInventoryState.currentCartId,
        name: editCartState.name,
        brand: editCartState.brand,
        status: editCartState.status,
        type: editCartState.type,
        notes: editCartState.notes,
      },
      true,
    );

    if (powerCartRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating cart status")));
      return;
    }

    dispatch(showSuccess(t("Cart status updated successfully")));

    void loadCarts();
    setEditCartState(prevState => ({ ...prevState, modalVisible: false }));
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setEditCartState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropDownChange(value: any, property: string) {
    setEditCartState(prevState => ({ ...prevState, [property]: value }));
  }

  function handleCartClick(id: number) {
    const cart = cartInventoryState.availableCarts.find(cart => cart.id === id);
    ReactDOM.unstable_batchedUpdates(() => {
      setCartInventoryState(prevState => ({ ...prevState, currentCartId: id }));
      setEditCartState(prevState => ({
        ...prevState,
        modalVisible: true,
        name: cart.name,
        status: cart.status,
        brand: cart.brand,
        type: cart.type,
        notes: cart.notes,
        available_at: cart.available_at,
      }));
      setSelectedTab(0);
    });

    void getAuditTrail(id);
  }

  async function getAuditTrail(id: number, offset: number = 0, limit: number = 10) {

    const res = await GetPowerCartAudits(
      { power_cart_id: id, offset: offset, limit: limit},
      false,
    );

    if (res.status !== StatusCode.OK) {
      showError(t("Failed to fetch audit trail"));
      return;
    }
    
    if (res.data.length === 0 && auditTrail.length > 0) {
      ReactDOM.unstable_batchedUpdates(() => {
        setFinalAuditPage(true);
        setAuditOffset(prevOffset => Math.max(prevOffset - auditLimit, 0));
      });
    } else {
      ReactDOM.unstable_batchedUpdates(() => {
        setAuditTrail(res.data);
        setFinalAuditPage(false);
      });
    }
  }

  async function exportPowerCartTrail() {
    const exportCartTrail = await GetPowerCartAuditTrailExport({power_cart_id: cartInventoryState.currentCartId, date_range_start: exportState?.dateRangeStart, date_range_end: exportState?.dateRangeEnd}, true);
    if(exportCartTrail.status !== StatusCode.OK){
      showError(t('Failed to retrieve exported audit trail'));
      return;
    }

    const exportLink = document.createElement("a");
    exportLink.setAttribute("href", "data:text/csv;charset=utf-8,".concat(encodeURI(exportCartTrail?.data as string)));
    exportLink.setAttribute(
      "download",
      `${(editCartState?.name ?? "UNKNOWN").toUpperCase()}_Audit_Trail_${moment(exportState?.dateRangeStart).format("YYYY-MM-DD")}_${moment(exportState?.dateRangeEnd).format("YYYY-MM-DD")}`
        .replace(/ /g, "_")
        .toUpperCase()
        .concat(".csv"),
    );
    exportLink.click();
  }

  function handleSelectExportDateRange() {
    setShowDateExportRange(true);
    setEditCartState(prevState => ({ ...prevState, modalVisible: false }));
  }

  return (
    <Page
      title={t("Cart Inventory")}
      subtitle={t("Edit details about the cart inventory")}
      primaryAction={primaryAction}
      secondaryActions={[
      {
        content: (
          <div className="flex gap-2 items-center">
            <FontAwesomeIcon icon={faFileExport} />
            {t('Export Cart List')}
          </div>
        ),
        action: () => setShowExportModal(true),
        },
      ]}
    >
      <Page.Section>
        <div className="cart-inventory-header mb-4 mt-4 flex justify-between">
          <span>{t("Available")}</span>
        </div>
        <div className="power-cart-cards">
          {cartInventoryState.availableCarts
            ?.filter((cart: IPowerCart) => cart.status !== "maintenance" && cart.status !== "broken")
            .map((cart: IPowerCart, index: number) => {
              return (
                <PowerCartCard
                  key={index}
                  powerCart={cart}
                  editing={cartInventoryState.editing}
                  onDelete={handleDelete}
                  onEdit={handleCartClick}
                  handleSetStatus={(id, status) => void handleChangeStatus(id, status)}
                />
              );
            })}
        </div>
      </Page.Section>
      <Page.Section>
        <div className="cart-inventory-header mb-4 mt-4">
          <span>{t("Out of Service")}</span>
        </div>
        <div className="power-cart-cards">
          {cartInventoryState.availableCarts
            ?.filter((cart: IPowerCart) => cart.status == "maintenance" || cart.status == "broken")
            .map((cart: IPowerCart, index: number) => {
              return (
                <PowerCartCard
                  key={index}
                  powerCart={cart}
                  editing={cartInventoryState.editing}
                  onDelete={handleDelete}
                  onEdit={handleCartClick}
                  handleSetStatus={(id, status) => void handleChangeStatus(id, status)}
                />
              );
            })}
        </div>
      </Page.Section>

      <Sheet
        title={t("Edit Cart")}
        open={editCartState.modalVisible}
        size="small"
        stacked
        closable
        overflow
        onCancel={() => {
          setEditCartState(prevState => ({ ...prevState, modalVisible: false, name: "", status: "" }));
          setSelectedTab(0);
          setAuditTrail([]);
          setAuditLimit(10);
          setAuditOffset(0);
        }}
        onOk={selectedTab === 0 ? updateCart : () => handleSelectExportDateRange()}
        okText={selectedTab === 0 ? t("Update") : t("Export History")}
        onSecondary={selectedTab === GENERAL_INFORMATION ? () => handleDelete(cartInventoryState.currentCartId) : undefined}
        secondaryText={selectedTab === GENERAL_INFORMATION ? t("Delete") : undefined}
      >
        <Tabs 
          tabs={tabs}
          selected={selectedTab}
          onSelect={tab => setSelectedTab(tab)}
        />

        {/* General Information */}
        {selectedTab === GENERAL_INFORMATION ? (
        <FormLayout>
          <FormLayout.Group>
            <Input label={t("Name")} value={editCartState.name} id="name" onChange={handleInputChange} />
            <Input label={t("Brand")} value={editCartState.brand} id="brand" onChange={handleInputChange} />
          </FormLayout.Group>
          <FormLayout.Group>
            <Select
              defaultValue={editCartState.status}
              label={t("Status")}
              onChange={(value: any) => handleDropDownChange(value, "status")}
            >
              {cartStatuses.map((status: string, index: number) => {
                return (
                  <Option key={index} name={status} value={status}>
                    <span>{capitalize(status)}</span>
                  </Option>
                );
              })}
            </Select>
            <Select
              defaultValue={editCartState.type}
              label={t("Type")}
              onChange={(value: any) => handleDropDownChange(value, "type")}
            >
              <Option key={0} name={"gas"} value={"gas"}>
                <span>{t("Gas")}</span>
              </Option>
              <Option key={1} name={"electric"} value={"electric"}>
                <span>{t("Electric")}</span>
              </Option>
            </Select>
          </FormLayout.Group>
          <FormLayout.Group>
            <Input label={t("Note")} value={editCartState.notes ?? ""} id="notes" onChange={handleInputChange} />
          </FormLayout.Group>
          {editCartState.available_at ? (
            <FormLayout.Group>
              <p>
                {" "}
                {t("Available At: ")}{moment.utc(editCartState.available_at, "YYYY-MM-DD HH:mm:ss").local().format("LLL")}{" "}
              </p>
            </FormLayout.Group>
          ) : null}
        </FormLayout>
        ) : null}

        {/* Audit Trail */}
        {selectedTab === HISTORY ? (
          <div>
            {auditTrail.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100px",
                }}
              >
                <div style={{ fontWeight: "bold" }}>{t("No Activity Yet")}</div>
                <div style={{ color: "#888", fontSize: "14px" }}>
                  {t("Once changes are made to this tee sheet, they will ")}
                </div>
                <div style={{ color: "#888", fontSize: "14px", marginBottom: "10px" }}>
                  {t("appear here for your review.")}
                </div>
              </div>
            ) : (
              <>
              <ul style={{ overflowY: "scroll", maxHeight: "400px", padding: "0" }}>
                {auditTrail.map((audit, index) => (
                  <React.Fragment key={audit.id}>
                    <li>
                      <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", overflowY: "scroll" }}>
                        <div className="audit-trail-note-container">
                          <div style={{ justifySelf: "center" }}>
                            <div className={`audit-trail-circle-${audit.action}`}>
                              {audit.action.startsWith('status') ? (
                                  <>
                                    {audit.action === "status_maintenance" ? (
                                      <FontAwesomeIcon
                                        icon={faCircleExclamation}
                                        className={`audit-trail-circle-${audit.action}-icon`}
                                      />
                                    ) : null}
                                    {audit.action === "status_broken" ? (
                                      <FontAwesomeIcon
                                        icon={faEngineWarning}
                                        className={`audit-trail-circle-${audit.action}-icon`}
                                      />
                                    ) : null}
                                    {audit.action === "status_reserved" ? (
                                      <FontAwesomeIcon
                                        icon={faHand}
                                        className={`audit-trail-circle-${audit.action}-icon`}
                                      />
                                    ) : null}
                                    {audit.action === "status_occupied" ? (
                                      <FontAwesomeIcon
                                        icon={faTire}
                                        className={`audit-trail-circle-${audit.action}-icon`}
                                      />
                                    ) : null}
                                    {audit.action === "status_available" ? (
                                      <FontAwesomeIcon
                                        icon={faCircleCheck}
                                        className={`audit-trail-circle-${audit.action}-icon`}
                                      />
                                    ) : null}
                                  </>
                              ) : (
                                <FontAwesomeIcon
                                  icon={faCircleCheck}
                                  className={`audit-trail-circle-${audit.action}-icon`}
                                />
                              )}
                            </div>
                            {index < auditTrail.length - 1 ? <div className="bm-audit-trail-line"></div> : null}
                          </div>
                          <div style={{ marginLeft: "18px" }}>
                            <div className="audit-trail-title">{audit.title}</div>
                            <div className="audit-trail-note">{audit.description}</div>
                          </div>
                        </div>
                        <div className="audit-trail-date">
                          <div>{moment(audit.created_at as MomentInput).format("MMMM Do YYYY, h:mm a")}</div>
                          <div>{audit.user_id?.full_name}</div>
                        </div>
                      </div>
                    </li>
                  </React.Fragment>
                ))}
              </ul>
              <div style={{ justifyContent: "space-between", display: "flex", marginTop: "10px" }}>
                <ButtonNew
                  type="secondary"
                  style={{ height: "45px", alignSelf: "center", marginRight: "auto", marginTop: "25px" }}
                  onClick={() => {
                    void getAuditTrail(cartInventoryState.currentCartId, auditOffset - auditLimit, auditLimit);
                    setAuditOffset(prevOffset => Math.max(prevOffset - auditLimit, 0));
                  }}
                  disabled={auditOffset === 0}
                ><FontAwesomeIcon icon={faArrowLeft}/>{t("Previous")}</ButtonNew>
                <SelectNew
                  id="audit-limit-select"
                  label={t("Audits per page")}
                  value={auditLimit}
                  onChange={value => {
                    setAuditLimit(value);
                    setAuditOffset(0);
                    void getAuditTrail(cartInventoryState.currentCartId, 0, value);
                  }}
                  options={[
                    { id: 5, label: 5},
                    { id: 10, label: 10 },
                    { id: 20, label: 20 },
                    { id: 50, label: 50},
                  ]}
                />
                <ButtonNew
                  type="secondary"
                  style={{ height: "45px", alignSelf: "center", marginLeft: "20px", marginTop: "25px" }}
                  onClick={() => {
                    void getAuditTrail(cartInventoryState.currentCartId, auditOffset + auditLimit, auditLimit);
                    setAuditOffset(prevOffset => prevOffset + auditLimit);
                  }}
                  disabled={auditTrail.length < auditLimit || finalAuditPage}
                  >{t("Next")}<FontAwesomeIcon icon={faArrowRight}/></ButtonNew>

              </div>
              </>
            )}
          </div>
        ) : null}
        <div className="audit-border-bottom"/>
      </Sheet>

      <Popup
        type="warning"
        open={cartInventoryState.deletePopupVisible}
        title={t("Delete Cart?")}
        description={t("Are you sure you want to delete this cart? This cannot be undone.")}
        closable
        onCancel={() => setCartInventoryState(prevState => ({ ...prevState, deletePopupVisible: false }))}
        onOk={deleteCart}
        cancelText={t("Cancel")}
        okText={t("Delete")}
      />

      <Sheet
        title={t("Select Date Range")}
        open={showDateExportRange}
        size="small"
        stacked
        closable
        overflow
        onCancel={() => setShowDateExportRange(false)}
        onOk={exportPowerCartTrail}
        okText={t("Export")}
      >
        <DatePickerInput
          months={2}
          position="left"
          startingDate={exportState.dateRangeStart}
          setStartingDate={setDateRangeStart}
          endingDate={exportState.dateRangeEnd}
          setEndingDate={setDateRangeEnd}
          showQuickOptions
        />
      </Sheet>
      <ExportPowerCarts open={showExportModal} onClose={() => setShowExportModal(false)} />
    </Page>
  );
}
